/**
 * Keeps an element mounted long enough to animate *out*.
 *
 * React unmounts as soon as a condition flips false, which means an exit
 * animation never gets to run — the node is gone before the first frame. This
 * splits "should it exist" from "should it look open", so a component can be
 * rendered-but-closing.
 *
 * The other half of the problem is the *enter* animation: setting the open state
 * in the same paint as the mount gives the browser no start state to interpolate
 * from, so it snaps. Hence the `requestAnimationFrame` — one frame closed, then
 * open, so the transition has two ends.
 */

import { useEffect, useState } from 'react'

/** Kept in step with the durations in styles.css. */
export const TRANSITION_MS = 220

export interface Presence {
  /** Render the element at all. Stays true through the exit animation. */
  shouldRender: boolean
  /** Drives the open/closed styles. Lags `shouldRender` by a frame on enter. */
  isVisible: boolean
}

export function usePresence(isOpen: boolean, durationMs = TRANSITION_MS): Presence {
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isVisible, setIsVisible] = useState(isOpen)

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      // Two frames, not one: the first commits the mounted-but-closed state, the
      // second flips to open. A single frame is unreliable — React can batch both
      // into the same paint, and the transition is skipped.
      let inner = 0
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setIsVisible(true))
      })
      return () => {
        cancelAnimationFrame(outer)
        cancelAnimationFrame(inner)
      }
    }

    setIsVisible(false)
    const timer = setTimeout(() => setShouldRender(false), durationMs)
    return () => clearTimeout(timer)
  }, [isOpen, durationMs])

  return { shouldRender, isVisible }
}

/**
 * Locks background scrolling while something modal is open.
 *
 * Compensates for the scrollbar's width so the page behind does not visibly jump
 * sideways the moment a sheet opens.
 */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return

    const { body } = document
    const previousOverflow = body.style.overflow
    const previousPadding = body.style.paddingRight
    const scrollbar = window.innerWidth - document.documentElement.clientWidth

    body.style.overflow = 'hidden'
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`

    return () => {
      body.style.overflow = previousOverflow
      body.style.paddingRight = previousPadding
    }
  }, [active])
}

/** Calls `onEscape` while active. Escape closing a modal is not optional. */
export function useEscapeKey(active: boolean, onEscape: () => void): void {
  useEffect(() => {
    if (!active) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onEscape()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [active, onEscape])
}
